import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Mail, Lock, User, ArrowRight } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../services/supabase'

const Auth = ({ onClose, redirectTo, defaultMode = 'signin', variant = 'page' }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isSignUp, setIsSignUp] = useState(defaultMode === 'signup')
  
  // Get redirect destination from props or URL params
  const getRedirectPath = () => {
    return redirectTo || searchParams.get('redirect') || '/'
  }
  const [showPassword, setShowPassword] = useState(false)
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    username: ''
  })
  
  const { signUp, signIn, signInWithGoogle, loading, error, resetPassword } = useAuth()
  const [resetEmailSent, setResetEmailSent] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    console.log('🔍 Auth form submitted:', { isSignUp, formData })
    
    try {
      if (isSignUp) {
        console.log('📝 Attempting signup with:', formData.email)
        const { data, error } = await signUp(formData.email, formData.password, {
          full_name: formData.fullName,
          username: formData.username
        })
        console.log('📊 Signup result:', { data, error })
        
        if (!error) {
          console.log('✅ Signup successful!')
          
          // Check if user is immediately logged in (no email verification)
          if (data.session) {
            console.log('🎉 User logged in immediately')
            const redirectPath = getRedirectPath()
            console.log('🔄 Redirecting to:', redirectPath)
            onClose ? onClose() : navigate(redirectPath)
          } else if (data.user && !data.session) {
            // Email verification required - show success message but don't close
            console.log('📧 Email verification required')
            // Auto-switch to sign in mode
            setTimeout(() => {
              setIsSignUp(false)
              setFormData(prev => ({ ...prev, fullName: '', username: '' }))
            }, 2000)
          }
        } else {
          console.error('❌ Signup error:', error)
        }
      } else {
        console.log('🔑 Attempting signin with:', formData.email)
        const { data, error } = await signIn(formData.email, formData.password)
        console.log('📊 Signin result:', { data, error })
        if (!error) {
          console.log('✅ Signin successful!')
          const redirectPath = getRedirectPath()
          console.log('🔄 Redirecting to:', redirectPath)
          onClose ? onClose() : navigate(redirectPath)
        } else {
          console.error('❌ Signin error:', error)
        }
      }
    } catch (err) {
      console.error('💥 Auth exception:', err)
    }
  }

  const handleForgotPassword = async () => {
    if (!formData.email) {
      // You could add a toast or error message here
      console.log('Please enter your email address first')
      return
    }
    
    try {
      const { error } = await resetPassword(formData.email)
      if (error) {
        console.error('Password reset error:', error)
      } else {
        setResetEmailSent(true)
        console.log('Password reset email sent successfully')
      }
    } catch (err) {
      console.error('Password reset exception:', err)
    }
  }

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true)
    try {
      // Pass the redirect path so OAuth callback knows where to go after sign-in
      const redirectPath = getRedirectPath()
      const { error } = await signInWithGoogle(redirectPath)
      if (error) {
        console.error('Google sign-in error:', error)
      }
      // On success, user will be redirected to Google's OAuth page
    } catch (err) {
      console.error('Google sign-in exception:', err)
    } finally {
      setGoogleLoading(false)
    }
  }

  return (
    <div
      className={
        variant === 'modal'
          ? 'w-full'
          : 'min-h-screen bg-white flex items-center justify-center px-6'
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={
          variant === 'modal'
            ? 'w-full max-w-lg'
            : 'w-full max-w-md'
        }
      >
        <div className={variant === 'modal' ? 'text-center mb-4 pt-6 px-6' : 'text-center mb-8'}>
          <h1 className={variant === 'modal' ? 'font-jersey text-2xl md:text-3xl text-black leading-tight mb-1' : 'font-jersey text-3xl md:text-4xl text-black leading-tight mb-2'}>
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className={variant === 'modal' ? 'text-sm text-black/60' : 'text-black/60'}>
            {isSignUp ? 'Join Valyfy to start building amazing projects' : 'Sign in to continue building'}
          </p>
        </div>

        <motion.form
          onSubmit={handleSubmit}
          className={
            variant === 'modal'
              ? 'bg-white/60 backdrop-blur-xl border border-black/10 rounded-2xl p-6 shadow-lg max-h-[82vh] overflow-y-auto'
              : 'bg-white/60 backdrop-blur-xl border border-black/10 rounded-2xl p-8 shadow-lg'
          }
          style={{ 
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)'
          }}
        >
          <div className={variant === 'modal' ? 'space-y-4' : 'space-y-6'}>
            {/* Google Sign-In Button */}
            <motion.button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={googleLoading || loading}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className={
                variant === 'modal'
                  ? 'w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-black/10 bg-white hover:bg-black/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                  : 'w-full flex items-center justify-center gap-3 px-4 py-4 rounded-xl border border-black/10 bg-white hover:bg-black/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              }
            >
              {googleLoading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              <span className="font-medium text-black/80">
                {googleLoading ? 'Connecting...' : 'Continue with Google'}
              </span>
            </motion.button>

            {/* Toggle to show email form (collapsed by default to favor Google) */}
            {!showEmailForm && (
              <button
                type="button"
                onClick={() => setShowEmailForm(true)}
                className="w-full text-center text-sm text-black/60 hover:text-black/80 underline underline-offset-2"
              >
                or continue with email
              </button>
            )}

            {showEmailForm && (
              <>
                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-black/10"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white/60 text-black/50">or continue with email</span>
                  </div>
                </div>
              </>
            )}

            {showEmailForm && isSignUp && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-black/60 uppercase tracking-wider mb-3">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-black/40" />
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className={
                        variant === 'modal'
                          ? 'w-full pl-12 pr-4 py-3 border border-black/10 rounded-xl focus:ring-2 focus:ring-vibe-cyan focus:border-transparent bg-white/50 backdrop-blur-sm placeholder-black/40'
                          : 'w-full pl-12 pr-4 py-4 border border-black/10 rounded-xl focus:ring-2 focus:ring-vibe-cyan focus:border-transparent bg-white/50 backdrop-blur-sm placeholder-black/40'
                      }
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-black/60 uppercase tracking-wider mb-3">
                    Username
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-black/40" />
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="Choose a username"
                      className={
                        variant === 'modal'
                          ? 'w-full pl-12 pr-4 py-3 border border-black/10 rounded-xl focus:ring-2 focus:ring-vibe-cyan focus:border-transparent bg-white/50 backdrop-blur-sm placeholder-black/40'
                          : 'w-full pl-12 pr-4 py-4 border border-black/10 rounded-xl focus:ring-2 focus:ring-vibe-cyan focus:border-transparent bg-white/50 backdrop-blur-sm placeholder-black/40'
                      }
                    />
                  </div>
                </div>
              </>
            )}

            {showEmailForm && (
            <>
            <div>
              <label className="block text-sm font-semibold text-black/60 uppercase tracking-wider mb-3">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-black/40" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className={
                    variant === 'modal'
                      ? 'w-full pl-12 pr-4 py-3 border border-black/10 rounded-xl focus:ring-2 focus:ring-vibe-cyan focus:border-transparent bg-white/50 backdrop-blur-sm placeholder-black/40'
                      : 'w-full pl-12 pr-4 py-4 border border-black/10 rounded-xl focus:ring-2 focus:ring-vibe-cyan focus:border-transparent bg-white/50 backdrop-blur-sm placeholder-black/40'
                  }
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-black/60 uppercase tracking-wider mb-3">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-black/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className={
                    variant === 'modal'
                      ? 'w-full pl-12 pr-12 py-3 border border-black/10 rounded-xl focus:ring-2 focus:ring-vibe-cyan focus:border-transparent bg-white/50 backdrop-blur-sm placeholder-black/40'
                      : 'w-full pl-12 pr-12 py-4 border border-black/10 rounded-xl focus:ring-2 focus:ring-vibe-cyan focus:border-transparent bg-white/50 backdrop-blur-sm placeholder-black/40'
                  }
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black/40 hover:text-black/60"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {!isSignUp && (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-black/60 hover:text-black/80 transition-colors"
                >
                  Forgot your password?
                </button>
              )}
            </div>
            </>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {resetEmailSent && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-green-600 text-sm">
                  ✅ Password reset email sent! Check your inbox and follow the instructions.
                </p>
              </div>
            )}



            <div className={variant === 'modal' ? 'space-y-3' : 'space-y-4'}>
              {showEmailForm && (
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={loading}
                  className={
                    variant === 'modal'
                      ? 'w-full bg-vibe-cyan text-black py-3 rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2'
                      : 'w-full bg-vibe-cyan text-black py-4 rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2'
                  }
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>{isSignUp ? 'Create Account' : 'Sign In'}</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </motion.button>
              )}

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setIsSignUp(!isSignUp)}
                  className="text-black/60 hover:text-black transition-colors"
                >
                  {isSignUp
                    ? 'Already have an account? Sign in'
                    : "Don't have an account? Sign up"
                  }
                </button>
              </div>
            </div>
          </div>
        </motion.form>
      </motion.div>
    </div>
  )
}

export default Auth